/**
 * =============================================================================
 * NavWorkspacesTree — Árvore de Workspaces → Pipelines do CRM (Navegação 2.0)
 * Versão: 1.0.0 (2026-08-05)
 *
 * Descrição:
 *   Reaproveita o visual antigo do bloco "WORKSPACES" da CrmSidebar (bolinha
 *   colorida do workspace, chevron de expandir/colapsar, pipelines indentados
 *   e destaque do pipeline ativo) dentro do painel de submenus da Navegação 2.0.
 *
 * Observações:
 *   - Rota dos pipelines idêntica à anterior: /tenant/crm/pipeline?id=<id>.
 *   - Estado expandido persiste na MESMA chave de localStorage já usada pela
 *     sidebar legada (`crm-expanded-workspaces`).
 *   - Dados vêm de useCrmWorkspacesHierarchy (RPC já existente), portanto as
 *     regras de visibilidade por workspace/pipeline permanecem inalteradas.
 * =============================================================================
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  useCrmWorkspacesHierarchy,
  type WorkspaceHierarchy,
} from '@/hooks/useCrmWorkspacesHierarchy';

/** Mesma chave da CrmSidebar legada (mantém o estado do usuário). */
const EXPANDED_WORKSPACES_KEY = 'crm-expanded-workspaces';

interface NavWorkspacesTreeProps {
  /** Callback após navegar (fecha o Sheet no mobile). */
  onNavigate?: () => void;
}

/** Item de um workspace com seus pipelines aninhados. */
function WorkspaceRow({
  workspace,
  isExpanded,
  onToggle,
  activePipelineId,
  onNavigate,
}: {
  workspace: WorkspaceHierarchy;
  isExpanded: boolean;
  onToggle: () => void;
  activePipelineId: string | null;
  onNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const workspaceColor = workspace.color || '#6366F1';
  const hasActivePipeline = workspace.pipelines.some((p) => p.id === activePipelineId);

  return (
    <div className="w-full">
      {/* Linha do workspace */}
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          hasActivePipeline ? 'text-sidebar-foreground' : 'text-sidebar-foreground/85',
        )}
      >
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-sm"
          style={{ backgroundColor: workspaceColor }}
        />
        <span className="flex-1 truncate font-medium">{workspace.name}</span>
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-70" />
        )}
      </button>

      {/* Pipelines do workspace */}
      {isExpanded && (
        <div className="flex flex-col gap-0.5 pb-1 pl-5">
          {workspace.pipelines.map((pipeline) => {
            const isActive = pipeline.id === activePipelineId;
            return (
              <button
                key={pipeline.id}
                type="button"
                onClick={() => {
                  navigate(`/tenant/crm/pipeline?id=${pipeline.id}`);
                  onNavigate?.();
                }}
                className={cn(
                  'flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                  'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                  isActive
                    ? 'bg-sidebar-primary/10 font-medium text-sidebar-primary'
                    : 'text-sidebar-foreground/75',
                )}
              >
                <span className="truncate">{pipeline.name}</span>
                {pipeline.is_default && (
                  <span className="shrink-0 text-[10px] uppercase tracking-wide text-sidebar-foreground/50">
                    padrão
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function NavWorkspacesTree({ onNavigate }: NavWorkspacesTreeProps) {
  const { workspaces, isLoading } = useCrmWorkspacesHierarchy();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Pipeline ativo (lido da URL, igual à sidebar legada).
  const activePipelineId = location.pathname.includes('/pipeline')
    ? searchParams.get('id')
    : null;

  // Estado expandido persistido em localStorage.
  const [expanded, setExpanded] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(EXPANDED_WORKSPACES_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Auto-expande o workspace que contém o pipeline ativo.
  useEffect(() => {
    if (!activePipelineId || workspaces.length === 0) return;
    const ws = workspaces.find((w) => w.pipelines.some((p) => p.id === activePipelineId));
    if (ws) {
      setExpanded((prev) => (prev.includes(ws.id) ? prev : [...prev, ws.id]));
    }
  }, [activePipelineId, workspaces]);

  // Persiste o estado expandido.
  useEffect(() => {
    try {
      localStorage.setItem(EXPANDED_WORKSPACES_KEY, JSON.stringify(expanded));
    } catch {
      /* localStorage indisponível — ignora */
    }
  }, [expanded]);

  const toggle = (id: string) =>
    setExpanded((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  if (isLoading) {
    return (
      <div className="space-y-1 px-2">
        {[1, 2].map((i) => (
          <Skeleton key={i} className="h-7 w-full rounded" />
        ))}
      </div>
    );
  }

  if (workspaces.length === 0) return null;

  return (
    <div className="flex flex-col gap-0.5">
      {workspaces.map((ws) => (
        <WorkspaceRow
          key={ws.id}
          workspace={ws}
          isExpanded={expanded.includes(ws.id)}
          onToggle={() => toggle(ws.id)}
          activePipelineId={activePipelineId}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
}
